import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCustomerDocuments } from "@/hooks/useCustomerPortal";
import { useIsMobile } from "@/hooks/use-mobile";
import { File, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

const formatSize = (bytes: number | null) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const CustomerDocuments = () => {
  const { data: documents, isLoading } = useCustomerDocuments();
  const isMobile = useIsMobile();

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>;
  }

  const items = documents ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Documents</h1>
        <p className="text-muted-foreground text-sm">{items.length} document{items.length !== 1 ? "s" : ""}</p>
      </div>

      {items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><File className="mx-auto h-10 w-10 mb-2 opacity-40" /><p>No documents shared yet</p></CardContent></Card>
      ) : isMobile ? (
        <div className="space-y-3">
          {items.map((doc) => (
            <Card key={doc.id}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{doc.title}</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span className="truncate mr-2">{doc.file_name}</span>
                  <span className="shrink-0">{formatSize(doc.file_size)}</span>
                </div>
                <p className="text-xs text-muted-foreground">{doc.created_at?.split("T")[0]}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell className="font-medium">{doc.title}</TableCell>
                    <TableCell className="text-muted-foreground">{doc.file_name}</TableCell>
                    <TableCell className="text-muted-foreground">{formatSize(doc.file_size)}</TableCell>
                    <TableCell className="text-muted-foreground">{doc.created_at?.split("T")[0]}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Download className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CustomerDocuments;
